"use client";

import { ArrowRight } from "iconoir-react";
import * as React from "react";
import { toast } from "sonner";

import { ApplyNowEmailCapture } from "@/components/forms/apply-now-email-capture";
import { EMAIL_CAPTURE_PILL_HEIGHT_CLASS } from "@/components/forms/email-capture-pill-styles";
import {
  useFundingEligibility,
  useFundingEligibilityIneligibleVisible,
  useFundingEligibilityStepTwoActive,
  useFundingEligibilityStepTwoGradient,
  useFundingEligibilitySuccessVisible,
} from "@/components/home/funding-eligibility-context";
import {
  FUNDING_ELIGIBILITY_APPLY_FORM_CLASS,
  FUNDING_ELIGIBILITY_APPLY_INPUT_CLASS,
  FUNDING_ELIGIBILITY_CROSSFADE,
  FUNDING_ELIGIBILITY_STEP_HANDOFF_MS,
} from "@/components/home/funding-eligibility-shared";
import { FundingEligibilityStepCard } from "@/components/home/funding-eligibility-step-card";
import type { LiveHomeCopy } from "@/content/pages/home/get-live-home-copy";
import { cn } from "@/lib/utils";

const GREY_500 = "text-[#7b8084]";

const BODY =
  "text-[14px] leading-[1.5] tracking-[-0.28px] lg:text-[16px] lg:tracking-[-0.02em]";

export function FundingEligibilitySubmitCard({
  copy,
  className,
}: {
  copy: LiveHomeCopy["fundingEligibility"];
  className?: string;
}) {
  const { applicationEmail, formValues, markStepTwoSubmitted } =
    useFundingEligibility();
  const showSuccessUi = useFundingEligibilitySuccessVisible();
  const showIneligibleUi = useFundingEligibilityIneligibleVisible();
  const isStepTwoActive = useFundingEligibilityStepTwoActive();
  const showStepTwoGradient = useFundingEligibilityStepTwoGradient();

  const eligibilityFields = React.useMemo(
    () => ({
      revenue: formValues.revenue,
      category: formValues.category,
      country: formValues.country,
    }),
    [formValues.category, formValues.country, formValues.revenue]
  );

  const ineligibleBodyCopy = showIneligibleUi
    ? copy.step02BodyIneligible
    : null;

  function handleIneligibleSubmitted() {
    toast.success(copy.ineligibleSubmitToastTitle, {
      description: copy.ineligibleSubmitToastDescription,
    });
  }

  function handleStepTwoSubmitted() {
    markStepTwoSubmitted();
    if (showIneligibleUi) {
      handleIneligibleSubmitted();
    }
  }

  const stepTitle = showIneligibleUi
    ? copy.step02TitleIneligible
    : copy.step02Title;

  const bodyCopy = ineligibleBodyCopy
    ? ineligibleBodyCopy
    : showSuccessUi
      ? copy.step02BodyAfterComplete
      : null;

  const desktopBodyCopy = bodyCopy ?? copy.step02BodyBeforeComplete;
  const mobileBodyCopy = bodyCopy ?? copy.step02BodyMobile;

  const emailPlaceholder = showIneligibleUi
    ? copy.ineligibleEmailPlaceholder
    : copy.emailPlaceholder;

  const applyLabel = showIneligibleUi
    ? copy.ineligibleApplyLabel
    : copy.applyNowLabel;

  return (
    <FundingEligibilityStepCard
      showGradient={showStepTwoGradient}
      className={className}
      contentClassName="flex min-h-full flex-1 flex-col gap-4 p-4 lg:gap-6 lg:p-6"
    >
      <div className="flex flex-col gap-4">
        <StepTitle index="02" title={stepTitle} />
        <p className={cn(BODY, GREY_500, "lg:hidden")}>{mobileBodyCopy}</p>
        <p className={cn(BODY, GREY_500, "hidden lg:block")}>
          {desktopBodyCopy}
        </p>
      </div>

      <div
        className={cn(
          "relative mt-auto w-full min-w-0 shrink-0",
          EMAIL_CAPTURE_PILL_HEIGHT_CLASS
        )}
      >
        <div
          aria-hidden={isStepTwoActive}
          className={cn(
            "absolute inset-0",
            FUNDING_ELIGIBILITY_CROSSFADE,
            isStepTwoActive
              ? "pointer-events-none opacity-0 blur-[2px]"
              : "opacity-100 blur-none"
          )}
        >
          <PassiveApplyField
            emailPlaceholder={emailPlaceholder}
            applyLabel={applyLabel}
          />
        </div>

        <div
          aria-hidden={!isStepTwoActive}
          className={cn(
            "absolute inset-0",
            FUNDING_ELIGIBILITY_CROSSFADE,
            isStepTwoActive
              ? "opacity-100 blur-none"
              : "pointer-events-none opacity-0 blur-[2px]"
          )}
        >
          <ApplyNowEmailCapture
            id="funding-apply-email"
            emailPlaceholder={emailPlaceholder}
            applyLabel={applyLabel}
            sendingLabel={copy.sendingLabel}
            eligibility={eligibilityFields}
            redirectToSignup={showSuccessUi}
            ctaIntent={showSuccessUi ? "apply" : undefined}
            ctaLocation="body"
            redirectDelayMs={
              showSuccessUi ? FUNDING_ELIGIBILITY_STEP_HANDOFF_MS : undefined
            }
            onSubmitted={handleStepTwoSubmitted}
            disabled={!isStepTwoActive}
            prefillEmail={applicationEmail}
            className="w-full min-w-0"
            formClassName={cn(
              FUNDING_ELIGIBILITY_APPLY_FORM_CLASS,
              "focus-within:border-grey-500 transition-[border-color] duration-200 ease-[cubic-bezier(0.23,1,0.32,1)]"
            )}
            inputClassName={FUNDING_ELIGIBILITY_APPLY_INPUT_CLASS}
            buttonLayout="hero-form"
          />
        </div>
      </div>
    </FundingEligibilityStepCard>
  );
}

function PassiveApplyField({
  emailPlaceholder,
  applyLabel,
}: {
  emailPlaceholder: string;
  applyLabel: string;
}) {
  return (
    <div aria-hidden className={FUNDING_ELIGIBILITY_APPLY_FORM_CLASS}>
      <span
        className={cn(
          FUNDING_ELIGIBILITY_APPLY_INPUT_CLASS,
          "pointer-events-none truncate text-[#b0b3b5]"
        )}
      >
        {emailPlaceholder}
      </span>
      <span className="bg-grey-100 text-grey-300 flex shrink-0 items-center gap-3 rounded-full py-1.5 pr-[7px] pl-4">
        <span className="text-[14px] leading-[1.4] font-medium whitespace-nowrap">
          {applyLabel}
        </span>
        <span className="bg-grey-200 flex size-7 shrink-0 items-center justify-center rounded-full">
          <ArrowRight className="text-grey-300 size-4" strokeWidth={2} />
        </span>
      </span>
    </div>
  );
}

function StepTitle({ index, title }: { index: string; title: string }) {
  return (
    <p className="text-grey-950 text-[18px] leading-[1.3] tracking-[-0.36px] lg:text-[20px] lg:tracking-[-0.04em]">
      <span className={GREY_500}>{index}. </span>
      {title}
    </p>
  );
}
